import { onError } from '@apollo/client/link/error'

import { notifyClient } from './client'

// The GraphQL reporting funnel: ONE link covers every operation call site, so
// components never call Airbrake for operation failures. User-facing copy is
// a separate link in the app's chain (errorToastLink), not the component's.
//
// Dedup by construction: an un-.catch()ed mutation promise also surfaces as
// an unhandledrejection, but the client notifier's filter drops
// ApolloError-typed notices arriving via the global handlers — so a GraphQL
// failure produces exactly one notice, from here, with richer context.
//
// PII: variables are sent ONLY for operations on the app-provided allowlist
// (verified ID/enum-only input shapes). Everything else sends variable KEY
// NAMES — shape without content. Response bodies are never sent: sign-in
// operation responses embed the auth token (selected explicitly beside
// UserFragment), and any body is unbounded PII surface.

interface ReportableGraphQLError {
  message: string
  path?: ReadonlyArray<string | number>
}

export interface ApolloErrorInput {
  graphQLErrors?: ReadonlyArray<ReportableGraphQLError>
  networkError?: (Error & { statusCode?: number }) | null
  // Undefined-tolerant on purpose: link mocks (and edge cases in the link
  // chain) can invoke handlers without an operation.
  operation?: {
    operationName?: string
    variables?: Record<string, unknown>
  } | null
}

// Pure and exported for tests: the exact notify payload for a failure.
// Returns null when there is nothing to report.
export function buildApolloErrorReport(
  input: ApolloErrorInput,
  variableSafeOperations: ReadonlySet<string>,
) {
  const hasGraphQLErrors = Boolean(input.graphQLErrors?.length)
  if (!hasGraphQLErrors && !input.networkError) return null

  const operationName = input.operation?.operationName ?? 'unknown'
  const variables = input.operation?.variables ?? {}

  return {
    // networkError carries a real stack; GraphQL-level failures get a
    // minted error whose message groups per operation.
    error: input.networkError ?? new Error(`GraphQL error: ${operationName}`),
    context: {
      component: 'apolloErrorLink',
      // Transport failure = defect (notifies). A 200-with-errors is the
      // backend deliberately rejecting — its own tracking already recorded
      // it server-side — so it stays dashboard-visible as a warning rate.
      severity: input.networkError ? 'error' : 'warning',
    },
    params: {
      operationName,
      graphQLErrors: input.graphQLErrors?.map((e) => ({
        message: e.message,
        path: e.path?.join('.'),
      })),
      statusCode: input.networkError?.statusCode,
      variables: variableSafeOperations.has(operationName)
        ? variables
        : { keys: Object.keys(variables) },
    },
  }
}

export function makeAirbrakeErrorLink(
  opts: { variableSafeOperations?: Iterable<string> } = {},
) {
  const safeOperations = new Set(opts.variableSafeOperations ?? [])

  return onError(({ graphQLErrors, networkError, operation }) => {
    const operationName = operation?.operationName ?? 'unknown'

    // Lean devtools logging: operation + messages ONLY. The previous link
    // dumped Authorization headers, full variables, and response bodies here
    // — which also leaked into breadcrumbs when console instrumentation was
    // on. Never log those.
    graphQLErrors?.forEach(({ message, path }) => {
      console.error(
        `[GraphQL error] ${operationName}${path ? ` at ${path.join('.')}` : ''}: ${message}`,
      )
    })
    if (networkError) {
      console.error(`[Network error] ${operationName}: ${networkError}`)
    }

    // Server-side apollo failures (gSSP direct queries) throw to their
    // callers and are reported by the server-side reporters — the browser
    // is this link's jurisdiction.
    if (typeof window === 'undefined') return

    const report = buildApolloErrorReport(
      { graphQLErrors, networkError, operation },
      safeOperations,
    )
    if (report) notifyClient(report)
  })
}
